import { defineStore } from 'pinia';
import { dayjs } from '@src/utils/time';
import { useLocalStorage } from '@src/utils/storage';
import { useStoreCity } from '@src/store/modules/city';
import { useStoreMapSettings } from '@src/store/modules/mapSettings';
import { getInfo, logout as logoutApi, type AuthInfoUser } from '@src/api/auth';
import { getCookie, isAdminTenant, isDevMode } from '@src/utils';
import { isFromCrmFamily, isFromCrmMobile } from '@src/utils/subApp';

export type UserInfo = AuthInfoUser;

export const useStoreUser = defineStore('user', () => {
    const storeCity = useStoreCity();
    const storeMapSettings = useStoreMapSettings();

    const auth = useLocalStorage('auth', {
        token: '',
    });
    const user = ref<Partial<UserInfo>>({});

    const setAuth = ({ token }: { token: string }) => {
        if (token && token !== auth.value.token) auth.value.token = token;
    };

    /** 生产环境从 cookie 恢复 token（开发环境走 URL query） */
    const bootstrapAuth = () => {
        if (isDevMode()) return;
        const token = getCookie('token');
        if (token) setAuth({ token });
    };

    /** 请求中的 getUser，白名单路由不阻塞导航，需去重避免连续跳转重复拉取 */
    let userFetchPromise: Promise<void> | null = null;

    const getUser = async () => {
        if (userFetchPromise) return userFetchPromise;

        userFetchPromise = getInfo()
            .then(({ user: userData }) => {
                user.value = userData;
            })
            .finally(() => {
                userFetchPromise = null;
            });

        return userFetchPromise;
    };

    const logout = async ({ useApiLogout = true } = {}) => {
        try {
            /* 接入微观E家：子应用退出登录时、通过参数传递通知父应用，由父应用操作登出事件 */
            if (isFromCrmFamily()) return (window as any).ipcRenderer?.send('subApp:logout', { subAppName: 'LITE_MAP' });
            /* 接入微观世界：子应用退出登录时、通过参数传递通知父应用，由父应用操作登出事件 */
            if (isFromCrmMobile()) return window.parent.postMessage({ type: 'subApp.logout' }, '*');

            if (useApiLogout && auth.value.token) {
                await logoutApi().catch(() => {});
            }
        } finally {
            // 清空用户认证信息
            auth.value = {
                token: '',
            };
            user.value = {};
            userFetchPromise = null;

            // 清空城市相关数据
            storeCity.resetCityState();
            storeMapSettings.resetMapSettings();

            // 清空本地登录态后刷新页面，由路由守卫重新判定（白名单放行 / 受保护路由弹登录框）
            setTimeout(() => {
                window.location.reload();
            }, 800);
        }
    };

    // 是否是超级管理员（用于区分是否为 608 租户）
    const isSuperMan = computed(() => user.value.superMan === 'Y');

    // 是否是管理员（用于 common 参数, true 为公共表, false 为个人表）
    const isAdmin = computed(() => isAdminTenant(user.value.tenantId));

    // 是否已开通会员（非超级管理员依据开通城市 vipEndTime 判定）
    const isVip = computed(() => {
        return !isSuperMan.value ? (storeCity.myCityMapList ?? []).some(v => dayjs().isBefore(v.vipEndTime)) : true;
    });

    // 是否已登录
    const isLoggedIn = computed(() => !!auth.value.token);

    return {
        auth,
        user,
        setAuth,
        bootstrapAuth,
        getUser,
        logout,
        isSuperMan,
        isAdmin,
        isVip,
        isLoggedIn,
    };
});
